import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { referenceAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import AddIncomeLocationModal from '../components/AddIncomeLocationModal';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const sortLocations = (items = []) =>
  [...items].sort((a, b) => {
    if ((a.sort_order || 0) !== (b.sort_order || 0)) {
      return (a.sort_order || 0) - (b.sort_order || 0);
    }
    if ((a.name || '').localeCompare(b.name || '') !== 0) {
      return (a.name || '').localeCompare(b.name || '');
    }
    return (a.location || '').localeCompare(b.location || '');
  });

const AdminIncomeLocationsPage = () => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationNames, setLocationNames] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name_id: '',
    location: '',
    coordinates: '',
    online: false,
    active: true,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [locationsRes, namesRes] = await Promise.all([
        referenceAPI.getIncomeLocations(),
        referenceAPI.getIncomeLocationNames(),
      ]);
      setLocations(sortLocations(toArray(locationsRes.data, 'income locations')));
      setLocationNames(sortLocations(toArray(namesRes.data, 'income location names')));
    } catch (err) {
      console.error('Error loading income locations:', err);
      notify.error(errorMessage(err, 'Failed to load income locations'));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenEdit = (location) => {
    fields.reset();
    const coordinateString =
      location.latitude && location.longitude
        ? `${location.latitude}, ${location.longitude}`
        : '';
    setSelectedLocation(location);
    setFormData({
      name_id: location.name_id ? String(location.name_id) : '',
      location: location.location || '',
      coordinates: coordinateString,
      online: Boolean(location.online),
      active: Boolean(location.active),
    });
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setSelectedLocation(null);
    setFormData({
      name_id: '',
      location: '',
      coordinates: '',
      online: false,
      active: true,
    });
    setEditDialogOpen(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    if (!selectedLocation) return;
    if (!fields.validate({ name_id: !formData.name_id && 'Location name is required' })) return;

    setSaving(true);
    try {
      const payload = {
        name_id: Number(formData.name_id),
        online: formData.online,
        active: Boolean(formData.active),
      };

      if (formData.online) {
        payload.location = null;
        payload.coordinates = '';
      } else {
        if (formData.location) {
          payload.location = formData.location;
        }
        payload.coordinates = formData.coordinates?.trim() || '';
      }

      await referenceAPI.updateIncomeLocation(selectedLocation.id, payload);
      handleCloseEdit();
      loadData();
    } catch (err) {
      console.error('Error updating income location:', err);
      notify.error(errorMessage(err, 'Failed to update income location'));
    } finally {
      setSaving(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (location) => setDeleteTarget(location);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await referenceAPI.deleteIncomeLocation(deleteTarget.id);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      console.error('Error deleting income location:', err);
      notify.error(errorMessage(err, 'Failed to deactivate income location'));
    }
  };

  const handleToggleActive = async (location) => {
    try {
      await referenceAPI.updateIncomeLocation(location.id, {
        active: !location.active,
      });
      loadData();
    } catch (err) {
      console.error('Error toggling active state:', err);
      notify.error(errorMessage(err, 'Failed to update status'));
    }
  };

  const locationName = (id) =>
    locationNames.find((name) => name.id === id)?.name || 'Unknown';

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 0 },
            mb: 3,
          }}
        >
          <Typography variant="h4">Income Locations</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddModal(true)}
            sx={{
              minWidth: { xs: 0, sm: 'auto' },
              width: { xs: 32, sm: 'auto' },
              height: { xs: 32, sm: 'auto' },
              px: { xs: 0, sm: 2 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Income Location
            </Box>
          </Button>
        </Box>
      </Box>

      <Box sx={tablePageScrollRegionSx}>
      <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Online</TableCell>
              <TableCell>Coordinates</TableCell>
              <TableCell align="center">Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={6} />}
            {locations.map((location) => (
              <TableRow key={location.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenEdit(location) },
                      { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(location), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell>{locationName(location.name_id)}</TableCell>
                <TableCell>
                  {location.location || (location.online ? 'Online' : '-')}
                </TableCell>
                <TableCell>{location.online ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  {location.latitude && location.longitude
                    ? `${location.latitude}, ${location.longitude}`
                    : '-'}
                </TableCell>
                <TableCell align="center">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Switch
                      checked={Boolean(location.active)}
                      onChange={() => handleToggleActive(location)}
                      color="primary"
                    />
                    {!location.active && (
                      <Chip label="Inactive" size="small" color="warning" />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && locations.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No income locations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <AddIncomeLocationModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        apiCall={referenceAPI.createIncomeLocation}
        onSuccess={() => {
          setOpenAddModal(false);
          loadData();
        }}
      />

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Income Location</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Autocomplete
              size="small"
              fullWidth
              disableClearable
              options={locationNames}
              getOptionLabel={(name) => name.name}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
              filterOptions={createPrefixFilter((name) => name.name)}
              value={locationNames.find((name) => String(name.id) === formData.name_id) ?? null}
              onChange={(e, newValue) => {
                fields.clear('name_id');
                setFormData((prev) => ({ ...prev, name_id: newValue ? String(newValue.id) : '' }));
              }}
              renderInput={(params) => <TextField {...params} label="Location Name" required {...fields.props('name_id')} />}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.online)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      online: e.target.checked,
                      // Clear location-specific data when switching to online
                      ...(e.target.checked
                        ? { location: '', coordinates: '' }
                        : {}),
                    }))
                  }
                />
              }
              label="Online Location"
            />

            {!formData.online && (
              <>
                <TextField
                  size="small"
                  name="location"
                  label="Location (City, State)"
                  value={formData.location}
                  onChange={handleEditChange}
                  fullWidth
                />
                <TextField
                  size="small"
                  name="coordinates"
                  label="Coordinates (Latitude, Longitude)"
                  value={formData.coordinates}
                  onChange={handleEditChange}
                  fullWidth
                  placeholder="e.g., 35.48086, -97.75584"
                  helperText="Format: latitude, longitude"
                />
              </>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.active)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      active: e.target.checked,
                    }))
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deactivate Income Location"
        message={`Deactivate income location "${deleteTarget?.name}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLabel="Deactivate"
        confirmColor="warning"
      />
    </Box>
  );
};

export default AdminIncomeLocationsPage;

